// Редактор плана зала: drag-and-drop столов на SVG-канве.
// Раскладка хранится одним settings-ключом floorMap (JSON) — атомарное
// сохранение кнопкой; терминал этот ключ игнорирует. Публичный сайт брони
// получает план через GET /v1/public/clubs/:slug (join с онлайн-столами).

import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import { useAuth } from '@/auth/AuthContext'
import { Button, ErrorState, Loading } from '@/components/ui'
import { FloorMap, resolveGlyphKind, tableFootprint, type FloorMapTable } from '@billiard/ui'
import {
  DEFAULT_FLOOR_CANVAS,
  FLOOR_MAP_KEY,
  FLOOR_SCALE_MAX,
  FLOOR_SCALE_MIN,
  normalizeFloorScale,
  parseFloorMap,
  type FloorMapLayout,
  type GameType,
  type SettingRow,
  type Table
} from '@/lib/config'

const GRID_STEP = 10 // шаг привязки при перетаскивании
const GRID_LINE = 50 // шаг видимой сетки-подложки

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max)
}

/** Позиция курсора в координатах канвы SVG (через инверсию screen CTM). */
function svgPoint(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
  const ctm = svg.getScreenCTM()
  if (!ctm) return { x: 0, y: 0 }
  const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse())
  return { x: p.x, y: p.y }
}

interface DragState {
  id: string
  svg: SVGSVGElement
  /** смещение точки захвата от центра стола (в единицах канвы) */
  dx: number
  dy: number
}

export function FloorPlanEditorTab(): React.JSX.Element {
  const query = useConfigQuery()
  const { isSuperAdmin, hasRole } = useAuth()
  const canEdit = isSuperAdmin || hasRole('owner', 'admin')

  const tables = useFetch<Table[]>('/v1/tables', query, [query?.clubId])
  const gameTypes = useFetch<GameType[]>('/v1/game-types', query, [query?.clubId])
  const settings = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const save = useMutate()

  const [layout, setLayout] = useState<FloorMapLayout | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const dragRef = useRef<DragState | null>(null)

  // Инициализация черновика из сохранённой настройки (не перетирая правки).
  useEffect(() => {
    if (settings.data == null || dirty || layout != null) return
    const saved = parseFloorMap(settings.data.find((s) => s.key === FLOOR_MAP_KEY)?.value)
    setLayout(saved ?? { version: 1, canvas: { ...DEFAULT_FLOOR_CANVAS }, tables: {} })
  }, [settings.data, dirty, layout])

  const tableList = useMemo(() => tables.data ?? [], [tables.data])
  const byId = useMemo(() => new Map(tableList.map((t) => [t.id, t])), [tableList])
  const gameTypeList = gameTypes.data ?? []
  const kindName = (code?: string | null): string =>
    code ? (gameTypeList.find((g) => g.code === code)?.name ?? code) : 'Универсальный'

  // Столы, размещённые на плане (есть запись в layout) / оставшиеся в лотке.
  const placed: FloorMapTable[] = useMemo(() => {
    if (!layout) return []
    const out: FloorMapTable[] = []
    for (const t of tableList) {
      const e = layout.tables[t.id]
      if (!e) continue
      out.push({
        id: t.id,
        name: t.name,
        kind: t.kind ?? null,
        x: e.x,
        y: e.y,
        rot: e.rot,
        scale: e.scale
      })
    }
    return out
  }, [layout, tableList])
  const tray = useMemo(() => tableList.filter((t) => !layout?.tables[t.id]), [tableList, layout])

  const moveTable = (id: string, x: number, y: number): void => {
    setLayout((prev) => {
      if (!prev?.tables[id]) return prev
      const cur = prev.tables[id]
      const t = byId.get(id)
      const gtName = gameTypeList.find((g) => g.code === t?.kind)?.name
      const fp = tableFootprint(resolveGlyphKind(t?.kind ?? null, gtName), cur.scale)
      const effW = cur.rot % 180 === 0 ? fp.w : fp.h
      const effH = cur.rot % 180 === 0 ? fp.h : fp.w
      const nx = clamp(Math.round(x / GRID_STEP) * GRID_STEP, effW / 2, prev.canvas.w - effW / 2)
      const ny = clamp(Math.round(y / GRID_STEP) * GRID_STEP, effH / 2, prev.canvas.h - effH / 2)
      if (nx === cur.x && ny === cur.y) return prev
      return { ...prev, tables: { ...prev.tables, [id]: { ...cur, x: nx, y: ny } } }
    })
  }

  const onTablePointerDown = (id: string, e: React.PointerEvent<SVGGElement>): void => {
    if (!canEdit || !layout) return
    const svg = e.currentTarget.ownerSVGElement
    const entry = layout.tables[id]
    if (!svg || !entry) return
    e.preventDefault()
    setSelectedId(id)
    const p = svgPoint(svg, e.clientX, e.clientY)
    dragRef.current = { id, svg, dx: p.x - entry.x, dy: p.y - entry.y }

    const onMove = (ev: PointerEvent): void => {
      const d = dragRef.current
      if (!d) return
      const q = svgPoint(d.svg, ev.clientX, ev.clientY)
      moveTable(d.id, q.x - d.dx, q.y - d.dy)
      setDirty(true)
    }
    const onUp = (): void => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  const addToPlan = (t: Table): void => {
    setLayout((prev) =>
      prev
        ? {
            ...prev,
            tables: {
              ...prev.tables,
              [t.id]: { x: prev.canvas.w / 2, y: prev.canvas.h / 2, rot: 0, scale: 1 }
            }
          }
        : prev
    )
    setSelectedId(t.id)
    setDirty(true)
  }

  const rotateSelected = (): void => {
    if (!selectedId) return
    setLayout((prev) => {
      const cur = prev?.tables[selectedId]
      if (!prev || !cur) return prev
      return {
        ...prev,
        tables: { ...prev.tables, [selectedId]: { ...cur, rot: (cur.rot + 90) % 360 } }
      }
    })
    setDirty(true)
  }

  /** Изменить масштаб выбранного стола на delta (клэмп 0.5–2 + возврат в канву). */
  const scaleSelected = (delta: number): void => {
    if (!selectedId) return
    setLayout((prev) => {
      const cur = prev?.tables[selectedId]
      if (!prev || !cur) return prev
      const next = normalizeFloorScale(cur.scale + delta)
      if (next === cur.scale) return prev
      const t = byId.get(selectedId)
      const gtName = gameTypeList.find((g) => g.code === t?.kind)?.name
      const fp = tableFootprint(resolveGlyphKind(t?.kind ?? null, gtName), next)
      const effW = cur.rot % 180 === 0 ? fp.w : fp.h
      const effH = cur.rot % 180 === 0 ? fp.h : fp.w
      // Подросший стол мог вылезти за канву — возвращаем центр в допустимые пределы.
      const x = clamp(cur.x, effW / 2, prev.canvas.w - effW / 2)
      const y = clamp(cur.y, effH / 2, prev.canvas.h - effH / 2)
      return { ...prev, tables: { ...prev.tables, [selectedId]: { ...cur, scale: next, x, y } } }
    })
    setDirty(true)
  }

  const removeSelected = (): void => {
    if (!selectedId) return
    setLayout((prev) => {
      if (!prev) return prev
      const rest = { ...prev.tables }
      delete rest[selectedId]
      return { ...prev, tables: rest }
    })
    setSelectedId(null)
    setDirty(true)
  }

  const onSave = async (): Promise<void> => {
    if (!layout) return
    const ok = await save.mutate(() =>
      api.put(`/v1/settings/${FLOOR_MAP_KEY}`, { value: JSON.stringify(layout) }, { query })
    )
    if (ok) {
      setDirty(false)
      settings.reload()
    }
  }

  const loading = tables.loading || gameTypes.loading || settings.loading || layout == null
  const error = tables.error ?? gameTypes.error ?? settings.error

  if (error) return <ErrorState message={error} />
  if (loading) return <Loading />

  const selected = selectedId ? byId.get(selectedId) : null
  const selectedScale = selectedId ? (layout.tables[selectedId]?.scale ?? 1) : 1

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          {selected ? (
            <>
              <span className="muted">
                {selected.name} · {kindName(selected.kind)}
              </span>
              <Button size="sm" onClick={rotateSelected}>
                Повернуть 90°
              </Button>
              <span className="floor-scale">
                <Button
                  size="sm"
                  title="Уменьшить стол"
                  disabled={selectedScale <= FLOOR_SCALE_MIN}
                  onClick={() => scaleSelected(-0.1)}
                >
                  −
                </Button>
                <span className="muted floor-scale__value">{Math.round(selectedScale * 100)}%</span>
                <Button
                  size="sm"
                  title="Увеличить стол"
                  disabled={selectedScale >= FLOOR_SCALE_MAX}
                  onClick={() => scaleSelected(0.1)}
                >
                  +
                </Button>
              </span>
              <Button size="sm" onClick={removeSelected}>
                Убрать с плана
              </Button>
            </>
          ) : (
            <span className="muted">
              Перетаскивайте столы по плану; клик по столу — выбрать. План виден клиентам на сайте
              брони и в Telegram.
            </span>
          )}
        </div>
        <div className="toolbar__right">
          {save.error && <span className="form-error">{save.error}</span>}
          <Button
            variant="primary"
            size="sm"
            disabled={!canEdit || !dirty || save.saving}
            onClick={() => void onSave()}
            title={canEdit ? undefined : 'Сохранение доступно владельцу и администратору'}
          >
            {save.saving ? 'Сохранение…' : 'Сохранить план'}
          </Button>
        </div>
      </div>

      <div className="floor-editor" style={{ touchAction: 'none' }}>
        <FloorMap
          layout={{ canvas: layout.canvas, tables: placed }}
          gameTypes={gameTypeList.map((g) => ({
            code: g.code,
            name: g.name,
            color: g.color ?? null
          }))}
          selectedId={selectedId}
          onTablePointerDown={onTablePointerDown}
        >
          <defs>
            <pattern
              id="fm-grid"
              width={GRID_LINE}
              height={GRID_LINE}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${GRID_LINE} 0 L 0 0 0 ${GRID_LINE}`}
                fill="none"
                className="fm-grid-line"
              />
            </pattern>
          </defs>
          <rect
            x={0}
            y={0}
            width={layout.canvas.w}
            height={layout.canvas.h}
            className="fm-floor"
            onClick={() => setSelectedId(null)}
          />
          <rect
            x={0}
            y={0}
            width={layout.canvas.w}
            height={layout.canvas.h}
            fill="url(#fm-grid)"
            pointerEvents="none"
          />
        </FloorMap>
      </div>

      <div className="floor-tray">
        <div className="floor-tray__title">
          Вне плана {tray.length > 0 ? `(${tray.length})` : ''}
        </div>
        {placed.length === 0 && tray.length > 0 && (
          <p className="muted">
            Кликните по столу, чтобы поставить его на план, затем расставьте перетаскиванием и
            нажмите «Сохранить план».
          </p>
        )}
        {tray.length === 0 ? (
          <span className="muted">Все столы размещены на плане.</span>
        ) : (
          <div className="floor-tray__chips">
            {tray.map((t) => (
              <Button size="sm" key={t.id} onClick={() => addToPlan(t)}>
                {t.name} · {kindName(t.kind)}
                {t.onlineBookable === false ? ' · не онлайн' : ''}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
